import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #animationTimeout;

  /** @type {(() => void) | undefined} */
  #cancelBackgroundClose;

  /** @type {HTMLElement | null} */
  #pinnedItem = null;

  /** @type {HTMLElement | null} */
  #pinnedTrigger = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(
      'pointerleave',
      () => this.#debouncedDeactivate(this.#state.activeItem),
      { signal: this.#abortController.signal }
    );

    this.overflowMenu?.addEventListener(
      'pointerleave',
      () => this.#debouncedDeactivate(this.#state.activeItem),
      { signal: this.#abortController.signal }
    );

    document.addEventListener('pointerdown', this.#handleOutsidePointerDown, {
      capture: true,
      signal: this.#abortController.signal,
    });
    document.addEventListener('keydown', this.#handleKeyDown, { signal: this.#abortController.signal });

    onDocumentLoaded(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    clearTimeout(this.#animationTimeout);
    this.#cancelBackgroundClose?.();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Keep a submenu open after an intentional click, or close it on a second click.
   * @param {MouseEvent} event
   */
  toggle = (event) => {
    if (!(event.target instanceof Element)) return;

    const moreSlot = event.target.closest('[slot="more"]');
    const trigger = moreSlot?.querySelector('button') ?? event.target.closest('[ref="menuitem"]');
    const item = moreSlot ? findMenuItem(moreSlot) : findMenuItem(event.target);
    const hasSubmenu = item && (findSubmenu(item) || moreSlot);
    if (!(trigger instanceof HTMLElement) || !item || !hasSubmenu) return;

    if (this.#pinnedItem === item && this.#pinnedTrigger === trigger) {
      this.#debouncedDeactivate.cancel();
      this.#deactivate(item, true);
      return;
    }

    this.#pinnedItem = item;
    this.#pinnedTrigger = trigger;
    trigger.ariaExpanded = 'true';
    this.activate(event);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();
    clearTimeout(this.#animationTimeout);

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';
    const moreTrigger = event.target.closest('[slot="more"]')?.querySelector('button');
    if (moreTrigger instanceof HTMLElement) moreTrigger.ariaExpanded = 'true';
    if (this.#pinnedItem === item && this.#pinnedTrigger) {
      this.#pinnedTrigger.ariaExpanded = 'true';
    }

    let submenu = findSubmenu(item);
    let overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      this.#cancelBackgroundClose?.();
      delete this.dataset.submenuClosed;
      // Give the restored background the same closed start frame as the submenu clip.
      this.refs.overflowMenu?.getBoundingClientRect();
    } else if (previouslyActiveItem && findSubmenu(previouslyActiveItem)) {
      this.#removeBackgroundAfterTransition();
    }

    const submenuHeight = submenu ? Math.max(submenu.offsetHeight, overflowMenuHeight) : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', submenu ? '1' : '0');
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   * @param {boolean} [force]
   */
  #deactivate = (item = this.#state.activeItem, force = false) => {
    if (!item || item != this.#state.activeItem) return;
    if (!force && (this.overflowHovered || item === this.#pinnedItem)) return;

    if (item === this.#pinnedItem) {
      this.#pinnedItem = null;
      if (this.#pinnedTrigger) this.#pinnedTrigger.ariaExpanded = 'false';
      this.#pinnedTrigger = null;
    }

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.#removeBackgroundAfterTransition();
    this.dataset.overflowExpanded = 'false';

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    if (item.slot === 'overflow') {
      const moreTrigger = this.querySelector('[slot="more"] button');
      if (moreTrigger instanceof HTMLElement) moreTrigger.ariaExpanded = 'false';
    }
    item.setAttribute('data-animating', '');

    clearTimeout(this.#animationTimeout);
    this.#animationTimeout = setTimeout(() => {
      item.removeAttribute('data-animating');
    }, Math.max(0, this.animationDelay - 150)); // Start header transition 150ms before submenu finishes

  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  #handleOutsidePointerDown = (event) => {
    const item = this.#pinnedItem;
    if (!item || !(event.target instanceof Node)) return;
    if (this.#pinnedTrigger?.contains(event.target)) return;
    if (item.parentElement?.contains(event.target)) return;

    this.#debouncedDeactivate.cancel();
    this.#deactivate(item, true);
  };

  #handleKeyDown = (event) => {
    if (event.key !== 'Escape') return;

    const item = this.#state.activeItem;
    if (!item) return;

    const trigger = item === this.#pinnedItem ? this.#pinnedTrigger : item;

    this.#debouncedActivateHandler.cancel();
    this.#debouncedDeactivate.cancel();
    this.#deactivate(item, true);
    trigger?.focus();
  };

  #removeBackgroundAfterTransition = () => {
    this.#cancelBackgroundClose?.();

    const overflowMenu = this.refs.overflowMenu;
    if (!overflowMenu) {
      this.dataset.submenuClosed = '';
      return;
    }

    const finish = () => {
      overflowMenu.removeEventListener('transitionend', handleTransitionEnd);
      this.dataset.submenuClosed = '';
      this.#cancelBackgroundClose = undefined;
    };
    const handleTransitionEnd = (event) => {
      if (event.pseudoElement === '::after' && event.propertyName === 'height') finish();
    };

    overflowMenu.addEventListener('transitionend', handleTransitionEnd);
    this.#cancelBackgroundClose = () => {
      overflowMenu.removeEventListener('transitionend', handleTransitionEnd);
      this.#cancelBackgroundClose = undefined;
    };
  };

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  const directItem = element.closest('[ref="menuitem"]');
  if (directItem instanceof HTMLElement) return directItem;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
